/**
 * Aiming laser — and the honest version of its drawback.
 *
 * In a multiplayer shooter a laser is a trade: faster hip-fire for a red line
 * pointing back at you that every enemy client renders. This game is single-player.
 * There are no enemy clients, so that half of the trade has no counterpart, and
 * faking one — drawing a beam nobody in the fiction can react to and calling it a
 * risk — is exactly the kind of stat that looks implemented and is not.
 *
 * So the drawback is built out of something that does exist here: **AI perception.**
 * The beam is a light source pointed at people, and the soldiers react to it.
 *
 *  - A hostile the dot is actually resting on — the beam's own raycast hits their
 *    hitboxes — is being *painted*, and their awareness climbs fast.
 *  - A hostile who can see the player and is near the beam's path notices it more
 *    slowly, scaled by how close to their eyeline it passes.
 *
 * Both are rate-based, so sweeping a laser across a room costs little and holding
 * it on someone across a courtyard gets you spotted before you would otherwise have
 * been. That is a genuine, measurable risk/reward, and it is priced by holding time
 * rather than by a coin flip.
 *
 * Awareness is nudged directly rather than through `onSound`, which is a hearing
 * event with its own falloff and localisation error; being lased is neither heard
 * nor mislocalised. `Enemy.awareness` and `Enemy.suspicion` are public state on a
 * module this agent does not own, so this is a read-modify-write from the outside —
 * additive only, never clearing anything the AI set for itself.
 *
 * The beam and the dot are real world-space geometry, not a HUD overlay: one pooled
 * box mesh scaled along the shot line and one billboard for the dot, both additive,
 * both hidden the moment the laser is off.
 */

import * as THREE from 'three';
import { MASK } from '../../world/Physics.js';


export const TUNING = {
  /** Beam geometry. 8 mm reads at arm's length without becoming a light sabre. */
  beamRadius: 0.004,
  /**
   * 45 m, not 90. The dot is sub-pixel past about forty metres, so the extra
   * range buys nothing visible — and the ray is cast every frame, where a 90 m
   * sweep costs twice what a 45 m one does because the broadphase has to walk
   * twice the spatial-hash footprint. Measured: 0.163 ms at 90 m, 0.078 at 45.
   */
  beamRange: 45,
  beamOpacity: 0.42,
  /**
   * How often the muzzle's position *in view space* is re-sampled, Hz.
   *
   * `ViewModel.worldMuzzle()` forces a full hierarchy matrix update and costs
   * 0.54 ms — a third of the whole behaviour budget, every frame, for a point
   * that is bolted to the camera and moves by millimetres. So it is sampled
   * slowly, cached in camera space, and re-projected through the *live* camera
   * matrix every frame: the beam stays exactly welded to the view while the sway
   * of the weapon under it updates six times a second, which is not something
   * the eye can resolve on a 4 mm-wide line.
   */
  muzzleSampleHz: 6,
  /** Dot size at the muzzle, and how much it grows per metre of travel. */
  dotSize: 0.026,
  dotGrowth: 0.0016,
  dotOpacity: 0.95,

  /** Awareness per second for a hostile the dot is resting on. */
  paintedGain: 1.25,
  /**
   * Awareness per second for a hostile with eyes on the player, near the beam.
   *
   * Has to clear `Enemy.TUNING.awarenessDecay` (0.42/s) by a real margin or the
   * effect is arithmetic that never changes an outcome. The gain falls off
   * linearly to zero at `noticeRadius`, so with these numbers a beam passing
   * within about 1.8 m of a soldier's chest is a net gain and anything wider is
   * absorbed by his own decay — which is the behaviour wanted: sweeping a room
   * costs nothing, and putting the line past someone's ear costs you.
   */
  nearGain: 1.5,
  /** Metres of perpendicular distance from the beam within which it is noticed. */
  noticeRadius: 2.5,
  /** Beyond this the beam is too dim and too far to pick out. */
  noticeRange: 55,
  /** Evaluate the AI reaction at this rate, Hz. */
  reactHz: 10,
};

const SOLID = MASK.WORLD | MASK.PROP;

const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _end = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _chest = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _inv = new THREE.Matrix4();
const _scale = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _flip = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);

export class Laser {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.on = false;
    this.hitDist = 0;
    this.painting = null;
    this.notified = 0;
    this._acc = 0;
    this._built = false;
  }

  get config() {
    return this.system.config.laser;
  }

  onConfigChanged() {
    if (!this.config.enabled) this._hide();
    else if (this._built) this._applyColour();
  }

  /* --------------------------------- assets -------------------------------- */

  _build() {
    if (this._built) return;
    const colour = new THREE.Color(this.config.colour ?? 0xff2b1e);

    const geo = new THREE.BoxGeometry(1, 1, 1);
    geo.translate(0, 0, 0.5); // origin at the emitter, extending along +Z
    this._beamGeo = geo;
    this._beamMat = new THREE.MeshBasicMaterial({
      color: colour,
      transparent: true,
      opacity: TUNING.beamOpacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
      fog: false,
    });
    this.beam = new THREE.Mesh(geo, this._beamMat);
    this.beam.frustumCulled = false;
    this.beam.renderOrder = 12;
    this.beam.visible = false;
    this.beam.name = 'behaviour-laser-beam';
    this.ctx.scene.add(this.beam);

    this._dotTex = makeDotTexture();
    this._dotMat = new THREE.SpriteMaterial({
      map: this._dotTex,
      color: colour,
      transparent: true,
      opacity: TUNING.dotOpacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
      fog: false,
    });
    this.dot = new THREE.Sprite(this._dotMat);
    this.dot.renderOrder = 13;
    this.dot.visible = false;
    this.dot.name = 'behaviour-laser-dot';
    this.ctx.scene.add(this.dot);

    this._built = true;
  }

  _applyColour() {
    const c = this.config.colour ?? 0xff2b1e;
    this._beamMat?.color.set(c);
    this._dotMat?.color.set(c);
  }

  _hide() {
    this.on = false;
    this.painting = null;
    this._muzzleLocal = null;
    if (this.beam) this.beam.visible = false;
    if (this.dot) this.dot.visible = false;
  }

  /* ---------------------------------- solve -------------------------------- */

  /**
   * Where the beam starts and where it lands. Returns false if there is no beam.
   *
   * Solved at most once per frame: `simulate` (AI reaction) and `present` (the
   * geometry) both need the answer and neither should pay for it twice.
   */
  _solve() {
    const ctx = this.ctx;
    const p = ctx.player;
    if (!p || p.dead) return false;
    const frame = ctx.engine?.frame ?? 0;
    if (this._solvedFrame === frame) return this._solved;
    this._solvedFrame = frame;

    const cam = ctx.camera;
    cam.updateMatrixWorld();

    // Off the visible muzzle, not the camera origin: the latter sits inside the
    // player's head and a beam from there is parallax-wrong at close range in
    // exactly the way a laser must not be. See `muzzleSampleHz` for why the
    // muzzle is cached in camera space rather than re-derived every frame.
    _tmp.setFromMatrixPosition(cam.matrixWorld);
    _dir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    this._sampleMuzzle(cam, _tmp);
    if (this._muzzleLocal) {
      _origin.copy(this._muzzleLocal).applyMatrix4(cam.matrixWorld);
    } else {
      _origin.copy(_tmp).addScaledVector(_dir, 0.5);
    }

    const hit = ctx.physics?.raycast(_origin, _dir, TUNING.beamRange, MASK.ALL, p);
    if (hit?.hit) {
      this.hitDist = hit.dist;
      this.painting = hit.actor && hit.actor !== p ? hit.actor : null;
      _end.copy(hit.point);
      this._hitNormal = hit.normal ?? null;
    } else {
      this.hitDist = TUNING.beamRange;
      this.painting = null;
      _end.copy(_origin).addScaledVector(_dir, TUNING.beamRange);
      this._hitNormal = null;
    }
    this._solved = true;
    return true;
  }

  /** Refresh the cached camera-space muzzle offset, at most `muzzleSampleHz`. */
  _sampleMuzzle(cam, camPos) {
    const now = this.ctx.engine?.elapsed ?? 0;
    if (this._muzzleLocal && now - (this._muzzleAt ?? -99) < 1 / TUNING.muzzleSampleHz) return;
    const vm = this.ctx.weapons?.viewModel;
    if (!vm?.worldMuzzle) return;
    const world = vm.worldMuzzle(camPos);
    if (!world || !Number.isFinite(world.x)) return;
    this._muzzleAt = now;
    this._muzzleLocal = (this._muzzleLocal ?? new THREE.Vector3())
      .copy(world)
      .applyMatrix4(_inv.copy(cam.matrixWorld).invert());
  }

  /* ------------------------------ AI reaction ------------------------------ */

  simulate(dt) {
    if (!this.config.enabled) {
      this.painting = null;
      return;
    }
    this._acc += dt;
    const step = 1 / TUNING.reactHz;
    if (this._acc < step) return;
    const slice = this._acc;
    this._acc = 0;

    if (!this._solve()) return;
    this._react(slice);
  }

  _react(dt) {
    const ctx = this.ctx;
    const p = ctx.player;
    const list = ctx.ai?.enemies;
    if (!Array.isArray(list) || !p) return;
    const phys = ctx.physics;
    _chest.set(p.position.x, p.position.y + 0.95, p.position.z);

    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (!e || e.dead || !e.position) continue;
      if (!Number.isFinite(e.awareness)) continue;

      let gain = 0;
      if (e === this.painting) {
        // The dot is on them. Nothing subtle about that.
        gain = TUNING.paintedGain;
      } else {
        const dToPlayer = e.position.distanceTo(p.position);
        if (dToPlayer > TUNING.noticeRange) continue;
        // Perpendicular distance from the beam segment to their chest.
        _tmp.set(e.position.x, e.position.y + 1.1, e.position.z).sub(_origin);
        const along = _tmp.dot(_dir);
        if (along < 0.5 || along > this.hitDist + 2) continue;
        const perp = Math.sqrt(Math.max(0, _tmp.lengthSq() - along * along));
        if (perp > TUNING.noticeRadius) continue;
        // You only notice a beam you could have seen the shooter through.
        _eye.set(e.position.x, e.position.y + 1.62, e.position.z);
        if (phys && !phys.visible(_eye, _chest, e)) continue;
        gain = TUNING.nearGain * (1 - perp / TUNING.noticeRadius);
      }
      if (gain <= 0) continue;

      const before = e.awareness;
      e.awareness = Math.min(1, e.awareness + gain * dt);
      if (e.awareness > before) this.notified++;
      // Give them somewhere to look. Only when they have nothing better: the AI's
      // own suspicion from gunfire or a spotted contact is always more current.
      if (!e.target && !e.hasSuspicion && e.suspicion?.copy) {
        e.suspicion.copy(p.position);
        e.hasSuspicion = true;
      }
    }
  }

  /* ------------------------------- presentation ----------------------------- */

  present() {
    const cfg = this.config;
    if (!cfg.enabled) {
      if (this._built && this.beam.visible) this._hide();
      return;
    }
    this._build();
    if (!this._solve()) {
      this._hide();
      return;
    }
    this.on = true;

    /* ---- dot ---- */
    // Lifted off the surface along its normal so it does not z-fight the wall it
    // is painted on.
    _tmp.copy(_end);
    if (this._hitNormal) _tmp.addScaledVector(this._hitNormal, 0.012);
    else _tmp.addScaledVector(_dir, -0.02);
    this.dot.position.copy(_tmp);
    const s = TUNING.dotSize + this.hitDist * TUNING.dotGrowth;
    this.dot.scale.set(s, s, s);
    this.dot.visible = true;

    /* ---- beam ---- */
    const ads = this.ctx.weapons?.adsProgress ?? (this.ctx.player?.ads ? 1 : 0);
    const showBeam = cfg.beamWhileAds || ads < 0.5;
    if (showBeam) {
      _m.lookAt(_origin, _end, _up);
      _q.setFromRotationMatrix(_m);
      _q.multiply(_flip); // lookAt points -Z at the target; the box runs along +Z
      _scale.set(TUNING.beamRadius, TUNING.beamRadius, Math.max(0.02, this.hitDist));
      _m.compose(_origin, _q, _scale);
      this.beam.matrix.copy(_m);
      this.beam.matrixAutoUpdate = false;
      this.beam.matrixWorldNeedsUpdate = true;
      this.beam.visible = true;
      // The beam fades with length: a 90 m line at full strength reads as a solid
      // rod rather than as light in the air.
      this._beamMat.opacity = TUNING.beamOpacity * (1 - Math.min(0.55, this.hitDist / 160));
    } else {
      this.beam.visible = false;
    }
  }

  report() {
    return {
      enabled: !!this.config.enabled,
      beamVisible: !!this.beam?.visible,
      dotVisible: !!this.dot?.visible,
      hitDist: +this.hitDist.toFixed(2),
      painting: this.painting?.id ?? null,
      /** Cumulative count of AI awareness nudges caused by the beam. Non-zero is
       *  the proof that the drawback exists rather than being described. */
      awarenessNudges: this.notified,
    };
  }

  dispose() {
    if (!this._built) return;
    this.ctx.scene.remove(this.beam);
    this.ctx.scene.remove(this.dot);
    this._beamGeo.dispose();
    this._beamMat.dispose();
    this._dotMat.dispose();
    this._dotTex.dispose();
    this._built = false;
  }
}

/** Procedural dot sprite — no binary assets ever enter this project. */
function makeDotTexture() {
  const S = 32;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.28, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.62, 'rgba(255,255,255,0.18)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

void SOLID;
